import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import CloseIcon from "@mui/icons-material/Close";
import PortalV2Select from "../../common/PortalV2Select";

const toInputDate = (value) => {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return moment(date).format("YYYY-MM-DD");
};

/**
 * Freezer-Frost filters — compact trigger + chips + floating panel (not a long bar).
 */
const FreezerFrostFilters = ({
  dates,
  setDates,
  week,
  setWeek,
  site,
  setSite,
  aaAlias,
  setAaAlias,
  filterOptions = {},
  onClear,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // Draft state so panel edits apply together
  const [draftDates, setDraftDates] = useState(dates);
  const [draftWeek, setDraftWeek] = useState(week || "");
  const [draftSite, setDraftSite] = useState(site || "");
  const [draftAlias, setDraftAlias] = useState(aaAlias || "");

  useEffect(() => {
    if (!open) return;
    setDraftDates(dates);
    setDraftWeek(week || "");
    setDraftSite(site || "");
    setDraftAlias(aaAlias || "");
  }, [open, dates, week, site, aaAlias]);

  useEffect(() => {
    const onDoc = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const weekOptions = useMemo(
    () => [
      { value: "", label: "All weeks" },
      ...(filterOptions.weeks || []).map((item) => ({ value: item.value, label: item.label })),
    ],
    [filterOptions.weeks]
  );
  const siteOptions = useMemo(
    () => [
      { value: "", label: "All sites" },
      ...(filterOptions.sites || []).map((item) => ({ value: item.value, label: item.label })),
    ],
    [filterOptions.sites]
  );
  const aliasOptions = useMemo(
    () => [
      { value: "", label: "All AA aliases" },
      ...(filterOptions.aaAliases || []).map((item) => ({ value: item.value, label: item.label })),
    ],
    [filterOptions.aaAliases]
  );

  const activeChips = useMemo(() => {
    const chips = [];
    if (week) {
      const meta = (filterOptions.weeks || []).find((item) => item.value === week);
      chips.push({ key: "week", label: meta?.label || `Week ${week}` });
    } else if (dates.start || dates.end) {
      const from = dates.start ? moment(dates.start).format("DD MMM YY") : "…";
      const to = dates.end ? moment(dates.end).format("DD MMM YY") : "…";
      chips.push({ key: "date", label: `${from} – ${to}` });
    }
    if (site) {
      const meta = (filterOptions.sites || []).find((item) => item.value === site);
      chips.push({ key: "site", label: meta?.label || "Site" });
    }
    if (aaAlias) chips.push({ key: "aaAlias", label: aaAlias });
    return chips;
  }, [week, dates, site, aaAlias, filterOptions]);

  const removeChip = (key) => {
    if (key === "week" || key === "date") {
      setWeek("");
      setDates({ start: null, end: null });
    }
    if (key === "site") setSite("");
    if (key === "aaAlias") setAaAlias("");
  };

  const applyWeekToDraft = (value) => {
    setDraftWeek(value);
    if (!value) {
      setDraftDates({ start: null, end: null });
      return;
    }
    const meta = (filterOptions.weeks || []).find((item) => item.value === value);
    const year = meta?.year || filterOptions.year || moment().isoWeekYear();
    const weekNum = Number(value);
    setDraftDates({
      start: moment().isoWeekYear(year).isoWeek(weekNum).startOf("isoWeek").toDate(),
      end: moment().isoWeekYear(year).isoWeek(weekNum).endOf("isoWeek").toDate(),
    });
  };

  const handleApply = () => {
    setWeek(draftWeek);
    setSite(draftSite);
    setAaAlias(draftAlias);
    setDates(draftDates);
    setOpen(false);
  };

  const handlePanelClear = () => {
    setDraftWeek("");
    setDraftSite("");
    setDraftAlias("");
    setDraftDates({ start: null, end: null });
  };

  return (
    <div className="ff-dash-filter" ref={rootRef}>
      <div className="ff-dash-filter__rail">
        {activeChips.length > 0 ? (
          <div className="ff-dash-filter__chips">
            {activeChips.map((chip) => (
              <button
                type="button"
                key={chip.key}
                className="ff-dash-filter__chip"
                onClick={() => removeChip(chip.key)}
                title="Remove filter"
              >
                <span>{chip.label}</span>
                <CloseIcon className="ff-dash-filter__chip-x" />
              </button>
            ))}
          </div>
        ) : (
          <span className="ff-dash-filter__hint">Refine sessions</span>
        )}

        <button
          type="button"
          className={`ff-dash-filter__trigger${open || activeChips.length ? " is-active" : ""}`}
          onClick={() => setOpen((prev) => !prev)}
          aria-expanded={open}
          aria-haspopup="dialog"
        >
          <FilterListOutlinedIcon />
          <span>Filters</span>
          {activeChips.length > 0 ? <em className="ff-dash-filter__count">{activeChips.length}</em> : null}
        </button>
      </div>

      {open ? (
        <div className="ff-dash-filter__panel" role="dialog" aria-label="Dashboard filters">
          <div className="ff-dash-filter__panel-head">
            <div>
              <strong>Filter sessions</strong>
              <p>Date, week, site and AA alias</p>
            </div>
            <button type="button" className="ff-dash-filter__panel-close" onClick={() => setOpen(false)} aria-label="Close">
              <CloseIcon fontSize="small" />
            </button>
          </div>

          <div className="ff-dash-filter__grid">
            <label className="ff-dash-filter__field">
              <span>Date From</span>
              <input
                type="date"
                value={toInputDate(draftDates.start)}
                onChange={(e) => {
                  setDraftWeek("");
                  const raw = e.target.value;
                  setDraftDates((prev) => ({
                    start: raw ? moment(raw).startOf("day").toDate() : null,
                    end: prev.end || (raw ? moment(raw).endOf("day").toDate() : null),
                  }));
                }}
              />
            </label>
            <label className="ff-dash-filter__field">
              <span>Date To</span>
              <input
                type="date"
                value={toInputDate(draftDates.end)}
                onChange={(e) => {
                  setDraftWeek("");
                  const raw = e.target.value;
                  setDraftDates((prev) => ({
                    start: prev.start,
                    end: raw ? moment(raw).endOf("day").toDate() : null,
                  }));
                }}
              />
            </label>
            <div className="ff-dash-filter__field ff-dash-filter__field--select">
              <PortalV2Select
                layout="field"
                label="Week Number"
                value={draftWeek}
                options={weekOptions}
                onChange={applyWeekToDraft}
                placeholder="All weeks"
              />
            </div>
            <div className="ff-dash-filter__field ff-dash-filter__field--select">
              <PortalV2Select
                layout="field"
                label="Site"
                value={draftSite}
                options={siteOptions}
                onChange={setDraftSite}
                placeholder="All sites"
              />
            </div>
            <div className="ff-dash-filter__field ff-dash-filter__field--select ff-dash-filter__field--wide">
              <PortalV2Select
                layout="field"
                label="AA Alias"
                value={draftAlias}
                options={aliasOptions}
                onChange={setDraftAlias}
                placeholder="All AA aliases"
                searchable
                searchPlaceholder="Search AA alias…"
              />
            </div>
          </div>

          <div className="ff-dash-filter__panel-foot">
            <button type="button" className="ff-dash-filter__btn-ghost" onClick={handlePanelClear}>
              Reset
            </button>
            <div className="ff-dash-filter__foot-actions">
              <button
                type="button"
                className="ff-dash-filter__btn-ghost"
                onClick={() => {
                  onClear();
                  setOpen(false);
                }}
              >
                Clear all
              </button>
              <button type="button" className="ff-dash-filter__btn-primary" onClick={handleApply}>
                Apply filters
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default FreezerFrostFilters;
